import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, switchMap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import {
  AuthState,
  LoginCredentials,
  LoginResponse,
  JwtPayload,
  TokenValidationResponse,
  RegistrationPayload,
} from '../../features/auth/models/auth.models';
import { User } from '../../models/user.model';
import { TokenStorageService } from './token-storage.service';
import { API_ENDPOINTS } from '../../constants/api.constants';
import { APP_CONSTANTS } from '../../constants/app.constants';
import { Permission } from '../../constants/permissions.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);

  // State signals
  private readonly userSignal = signal<User | null>(null);
  private readonly tokenSignal = signal<string | null>(null);
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // Computed signals
  readonly user = this.userSignal.asReadonly();
  readonly token = this.tokenSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.userSignal() && !!this.tokenSignal());
  readonly userPermissions = computed(() => this.userSignal()?.permissions ?? []);

  constructor() {
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    const token = this.tokenStorage.getToken();
    const user = this.tokenStorage.getUser<User>();

    if (token && user) {
      this.tokenSignal.set(token);
      this.userSignal.set(user);
    }
  }

  private mapJwtPayloadToUser(payload: JwtPayload): User {
    console.log('Mapping JWT payload to User object:', payload);
    return {
      id: payload.user_id,
      email: payload.email,
      emailVerified: payload.emailVerified,
      name: payload.name,
      givenName: payload.givenName,
      familyName: payload.familyName,
      nickname: payload.nickname,
      picture: payload.picture,
      role: payload.role,
      roles: payload.roles,
      permissions: payload.permissions as Permission[],
      blocked: payload.blocked,
      bidderId: payload.bidder_id,
    };
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    return this.http.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials).pipe(
      tap((response) => {
        const token = response.access_token;

        // Decode the JWT to get user data
        const decodedToken = jwtDecode<JwtPayload>(token);
        console.log('Decoded JWT Payload:', decodedToken);
        const user = this.mapJwtPayloadToUser(decodedToken);

        // Store token and user data
        this.tokenSignal.set(token);
        this.userSignal.set(user);
        this.tokenStorage.setToken(token);
        this.tokenStorage.setUser(user);
        this.isLoadingSignal.set(false);
      }),
      catchError((error) => {
        const errorMessage = error.error?.message || 'Login failed. Please try again.';
        this.errorSignal.set(errorMessage);
        this.isLoadingSignal.set(false);
        throw error;
      })
    );
  }

  logout(): void {
    this.userSignal.set(null);
    this.tokenSignal.set(null);
    this.errorSignal.set(null);
    this.tokenStorage.clear();
    this.router.navigate(['/login']);
  }

  hasPermission(permission: Permission): boolean {
    return this.userPermissions().includes(permission);
  }

  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every((permission) => this.hasPermission(permission));
  }

  validateToken(token: string): Observable<TokenValidationResponse> {
    return this.http.get<TokenValidationResponse>(API_ENDPOINTS.AUTH.VALIDATE_TOKEN(token));
  }

  register(payload: RegistrationPayload): Observable<LoginResponse> {
    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    // Register user, then automatically login
    return this.http.post<void>(API_ENDPOINTS.AUTH.REGISTER, payload).pipe(
      switchMap(() =>
        this.login({
          email: payload.email,
          password: payload.password,
        })
      ),
      catchError((error) => {
        const errorMessage = error.error?.message || 'Registration failed. Please try again.';
        this.errorSignal.set(errorMessage);
        this.isLoadingSignal.set(false);
        throw error;
      })
    );
  }

  clearError(): void {
    this.errorSignal.set(null);
  }

  /**
   * Updates the current user's profile information
   * Used after profile updates to keep the local state in sync
   */
  updateUserProfile(updates: Partial<User>): void {
    const currentUser = this.userSignal();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates };
      this.userSignal.set(updatedUser);
      this.tokenStorage.setUser(updatedUser);
    }
  }
}
